import { useEffect, useState } from "react";
import { Box, Container, Grid } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { useHistory, useParams } from "react-router-dom";
import { decode } from "js-base64";

/** API */
import AnnounceApi from "../../api/home/announce.api";

/** COMPONENT */
import Loading from "../../component/loading/loading";
import AnnounceFileList from "./components/AnnounceFileList";
import AnnounceImageSlider from "./components/AnnounceImageSlider";
import AnnounceMetaStrip from "./components/AnnounceMetaStrip";
import { AnnounceLinkList } from "../announce/components/AnnounceLinks";

/** UTILS */
import { colors } from "../../constants/colors";
import { routeName } from "../../routes/routers-name";
import { getProfile } from "../../utils/app.utils";
import {
  announceChipLabel,
  getAnnounceMeta,
  splitAnnounceFiles,
} from "./components/announceView.utils";

/** STYLE */
import { CardHomepage } from "./home.style";
import {
  AnnounceChip,
  AnnouncePanel,
  AnnounceRichText,
  AnnounceTitle,
  BackPill,
  PageHeading,
  SectionHeading,
} from "./components/announceView.style";

export default function Detailinfo() {
  const [detail, setDetail] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const history = useHistory();
  const profile: any = JSON.parse(getProfile() || "{}");
  const roleId = JSON.parse(decode(profile))?.roleId;

  const { id } = useParams<any>();

  const getDatadetail = async () => {
    setLoading(true);
    await AnnounceApi.details(+id)
      .then((res) => {
        setDetail(res?.data || {});
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (id) {
      getDatadetail();
    }
  }, [id]);

  /** pdf เปิดแท็บใหม่, รูปแสดงในสไลด์คอลัมน์ขวาอยู่แล้ว จึงเปิดแท็บใหม่เหมือนกัน */
  const handlepreview = (file: any) => {
    if (!file?.filePath || !file?.fileName) return;
    window.open(
      `${process.env.REACT_APP_FILE_URL}${file.filePath}${file.fileName}`,
      "_blank"
    );
  };

  const isRent = detail?.announceType === "RENT";
  const { images, documents } = splitAnnounceFiles(detail?.fileUpload);

  return (
    <CardHomepage>
      <Container maxWidth="xl" className="py-4">
        <BackPill onClick={() => history.push(routeName.home)}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          หน้าหลัก / ข่าวสาร
        </BackPill>

        <PageHeading sx={{ marginTop: "20px", marginBottom: "24px" }}>
          {isRent ? "แจ้งประกาศแผงค้า" : "แจ้งประกาศจากระบบ"}
        </PageHeading>

        <AnnouncePanel>
          {/* หัวการ์ด — ป้ายประเภท + หัวข้อ + ปุ่มจองแผงค้า (ผู้เช่าเท่านั้น) */}
          <Box sx={{ padding: { xs: "20px 18px", md: "26px 32px" } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <AnnounceChip>
                <CampaignOutlinedIcon sx={{ fontSize: 18 }} />
                {announceChipLabel(detail)}
              </AnnounceChip>

              {isRent && roleId === 4 && (
                <Box
                  onClick={() =>
                    history.push(routeName.marketMap, {
                      announceId: detail?.announceId,
                      round: detail?.round,
                    })
                  }
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    backgroundColor: colors.themeMainColor,
                    color: colors.white,
                    fontFamily: "Kanit",
                    fontSize: "16px",
                    borderRadius: "8px",
                    padding: "9px 18px",
                    cursor: "pointer",
                    transition: "0.2s",
                    ":hover": { opacity: 0.88 },
                  }}
                >
                  <StorefrontOutlinedIcon sx={{ fontSize: 20 }} />
                  จองแผงค้า
                </Box>
              )}
            </Box>

            <AnnounceTitle sx={{ marginTop: "14px" }}>
              {detail?.title || "-"}
            </AnnounceTitle>
          </Box>

          {/* borderTop แทน <Divider/> เพราะ custom.css ทับ margin/สีด้วย !important */}
          <Box sx={{ borderTop: `1px solid ${colors.black_08}` }} />
          <AnnounceMetaStrip
            items={getAnnounceMeta(detail, "ช่วงเวลาเปิดให้เช่าแผง")}
          />
          <Box sx={{ borderTop: `1px solid ${colors.black_08}` }} />

          <Box sx={{ padding: { xs: "20px 18px", md: "28px 32px" } }}>
            <Grid container spacing={4}>
              <Grid item lg={images.length ? 6 : 12} xs={12}>
                <SectionHeading>รายละเอียดประกาศ</SectionHeading>
                <AnnounceRichText className="ck-content">
                  <div
                    dangerouslySetInnerHTML={{ __html: detail?.detail || "-" }}
                  />
                </AnnounceRichText>

                <AnnounceFileList
                  files={documents}
                  onPreview={handlepreview}
                />
                <AnnounceLinkList announceLinks={detail?.announceLinks} />
              </Grid>

              {images.length > 0 && (
                <Grid item lg={6} xs={12}>
                  <AnnounceImageSlider images={images} />
                </Grid>
              )}
            </Grid>
          </Box>
        </AnnouncePanel>
      </Container>
      {loading && <Loading show={loading} type="fullLoading" />}
    </CardHomepage>
  );
}
